// CapabilityEditor — the ONE grouped-cascade capability surface (paradigm A): the
// body of the role create wizard's Capabilities step and the role edit page, the
// two lists of an application credential (acts as itself · acts for a user), and
// the personal-access-token picker. Data-driven off the typed catalogue (ADR-025):
// the real expandIncludes / unmetRequires resolve the includes cascade + inert
// grants, so there are ZERO hardcoded parent→child gates. The IA (sections ·
// families · scale-vs-adjunct · which keys carry help) lives in presentation.ts;
// the catalogue supplies the relationships. All user-facing copy is i18n (the
// `permissions` namespace) — presentation holds keys, resolved here with t().
//
// One editor, many surfaces: each consumer passes the catalogue subset it may
// grant (`offeredKeys` — never a hand list) and, where a key carries a narrowing
// list, the `renderNarrowing` slot supplies what hangs under it (managed roles ·
// the responsibility level-set · device-target kinds). A behaviour change here
// lands on every surface at once — the point of the unification.
//
// Behaviors: an access scale (None < View < Manage) per resource; includes shown
// as "included"; requires unmet ⇒ inert (amber) + a one-click Fix that grants the
// prerequisite (never auto-added — the admin makes the widening choice); a key the
// grantor cannot give (`disabledKeys` — a PAT's not-held reads) is shown greyed
// with a "Not held" chip; entitlement off ⇒ keys/families hidden behind a quiet
// "module off" chip; draft modules ⇒ "coming soon"; tiered help (Tier-1 family
// intro on expand · Tier-2 per-key help behind the "i" only where the label can't carry the
// meaning).
//
// The yields / will-drop summary is NOT here — it lives where the commit surface is
// (a page header + sticky footer); derive it with capabilitySummary().

import { useMemo, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import {
  ALL_PERMISSION_KEYS,
  expandIncludes,
  getCapability,
  unmetRequires,
  type EntitlementFlag,
  type PermissionKey,
} from '@ems-app/schemas/permissions';
import { SECTIONS, filterSections, keysOfThing, type Family, type ScaleThing, type Thing, type ToggleThing } from './presentation.js';
import { icons } from '../../ui/icons.js';
import { Badge } from '../../ui/Badge.js';
import { Checkbox } from '../../ui/Checkbox.js';
import { Toggle } from '../../ui/Toggle.js';
import { SegmentedControl } from '../../ui/SegmentedControl.js';
import { Disclosure } from '../../ui/Disclosure.js';
import { InfoPopover } from '../../ui/InfoPopover.js';
import { formatNumber } from '../../../lib/format.js';

export interface CapabilityEditorProps {
  /** Granted capability keys (controlled). */
  grants: string[];
  onGrantsChange: (keys: string[]) => void;
  /** The catalogue subset THIS surface may grant — PROJECT_GRANTABLE_KEYS (a role ·
   *  an application acting for a user), API_CLIENT_SELF_GRANTABLE_KEYS (an
   *  application acting as itself — adds the machine contracts), PAT_GRANTABLE_KEYS
   *  (the read tier). Never hand-listed. */
  offeredKeys: readonly string[];
  /** Keys shown but UNSELECTABLE, chipped "Not held" (a token offering the whole
   *  read tier while greying what its owner doesn't hold in the pinned project). */
  disabledKeys?: ReadonlySet<string>;
  /** The project's optional-module ON-set — the editor only READS it. A credential
   *  surface passes ALL_ENTITLEMENTS_ON (nothing hidden). */
  entitlements: ReadonlySet<EntitlementFlag>;
  readOnly?: boolean;
  /** The capability + narrowing-list slot (ems-domain PERMISSIONS §Capability +
   *  narrowing list): what to render UNDER a key that carries a narrowing —
   *  the managed roles and the level-set under users:manage, the
   *  device-target kinds under device_targets:read. Called for every toggle and
   *  adjunct with its granted state; return null where nothing applies. A list
   *  returned while its key is OFF (a stored narrowing — the dormant state) opens
   *  its family by rule, so it never hides behind a collapsed, unbadged row. */
  renderNarrowing?: (key: PermissionKey, granted: boolean) => ReactNode;
  /** The section headings' level (default 2 — under a page's h1). A drawer whose
   *  dialog title is an h3 passes 4 so the outline nests: its band h3 → sections
   *  h4 → family triggers h5. */
  headingLevel?: 2 | 3 | 4;
}

const NONE = 'none'; // SegmentedControl sentinel for "no access" (never a real key)
const CATALOGUE_SET = new Set<string>(ALL_PERMISSION_KEYS);

/** A selective Tier-2 help affordance — only where a label can't carry the meaning:
 *  the words behind the "i" (a tap away — never a hover alone), never placed inside
 *  another control's label/button (no nested interactives). The accessible name
 *  carries the key's label, so twenty hints don't all read "More information".
 *  `text` is already translated by the caller. */
function HelpHint({ text, label }: { text: string; label: string }) {
  const { t } = useTranslation('permissions');
  return <InfoPopover label={t('editor.moreInfoAbout', { label })}>{text}</InfoPopover>;
}

/** The effective-capability + will-drop counts — for a page header + footer (the
 *  editor body doesn't render them; the commit surface owns them). */
export function capabilitySummary(grants: string[]): { yields: number; willDrop: number } {
  const expanded = expandIncludes(grants);
  const yields = [...expanded].filter((k) => getCapability(k) && !getCapability(k)?.platformTier).length;
  return { yields, willDrop: unmetRequires(grants).length };
}

export function CapabilityEditor({
  grants,
  onGrantsChange,
  offeredKeys,
  disabledKeys,
  entitlements,
  readOnly = false,
  renderNarrowing,
  headingLevel = 2,
}: CapabilityEditorProps) {
  const { t } = useTranslation('permissions');
  const SectionHeading: 'h2' | 'h3' | 'h4' = `h${headingLevel}`;
  const disclosureLevel = (headingLevel + 1) as 3 | 4 | 5;
  const offeredSet = useMemo(() => new Set(offeredKeys), [offeredKeys]);
  const sections = useMemo(() => filterSections(SECTIONS, offeredSet), [offeredSet]);
  const grantSet = useMemo(() => new Set(grants), [grants]);
  const expanded = useMemo(() => expandIncludes(grants), [grants]);
  const inert = useMemo(() => unmetRequires(grants), [grants]);
  const inertSet = useMemo(() => new Set(inert.map((x) => x.key)), [inert]);

  // Emit in catalogue order — a stable, diff-friendly array (the audit snapshot
  // doesn't churn on an untick + re-tick); anything the catalogue doesn't know
  // keeps its place at the end.
  const commit = (next: Set<string>) =>
    onGrantsChange([...ALL_PERMISSION_KEYS.filter((k) => next.has(k)), ...[...next].filter((k) => !CATALOGUE_SET.has(k))]);
  const setScale = (rungKeys: string[], selected: string | null) => {
    const n = new Set(grantSet);
    rungKeys.forEach((k) => n.delete(k));
    if (selected) n.add(selected);
    commit(n);
  };
  const addKeys = (...ks: string[]) => {
    const n = new Set(grantSet);
    ks.forEach((k) => n.add(k));
    commit(n);
  };
  const toggleKey = (k: string, on: boolean) => {
    const n = new Set(grantSet);
    if (on) n.add(k);
    else n.delete(k);
    commit(n);
  };

  const notHeld = (key: string): boolean => disabledKeys?.has(key) ?? false;
  // The not-held reason rides INSIDE the control's label (its accessible name), so
  // a disabled row announces why — a sibling chip would be invisible to AT.
  const withNotHeld = (label: string, held: boolean): ReactNode =>
    held ? (
      label
    ) : (
      <span className="inline-flex items-center gap-1.5">
        {label}
        <Badge variant="neutral" appearance="subtle">
          {t('editor.notHeld')}
        </Badge>
      </span>
    );
  // A Fix may only grant what this surface offers AND the grantor holds — never a
  // key it couldn't tick by hand.
  const fixable = (missing: readonly string[]): boolean => missing.every((k) => offeredSet.has(k) && !notHeld(k));
  const entOff = (key: string): boolean => {
    const e = getCapability(key)?.entitlement;
    return e !== undefined && !entitlements.has(e);
  };
  const reqMet = (key: string): boolean => (getCapability(key)?.requires ?? []).every((r) => expanded.has(r));
  const moduleLabel = (flag: EntitlementFlag) => t(`module.${flag}`);
  // Contextual label ("Assets: View") so a cross-scale "needs X" is unambiguous.
  const labelOf = (key: string): string => {
    const m = keyMeta.get(key);
    if (!m) return key;
    return 'scaleKey' in m ? `${t(m.scaleKey)}: ${t(m.rungKey)}` : t(m.labelKey);
  };
  const joinLabels = (keys: readonly string[]) => keys.map((k) => labelOf(k)).join(', ');

  const fixLink = (missing: readonly string[], className: string, label: string): ReactNode =>
    !readOnly && fixable(missing) ? (
      <button type="button" className={className} onClick={() => addKeys(...missing)}>
        {label}
      </button>
    ) : null;

  // ── renderers ──────────────────────────────────────────────────────────────
  const renderScale = (th: ScaleThing, famLabel: string): ReactNode => {
    const rungs = th.rungs.filter((r) => !entOff(r.key));
    const currentKey = [...rungs].reverse().find((r) => grantSet.has(r.key))?.key ?? null;
    const options = [
      { value: NONE, label: t(th.noneLabel ?? 'editor.none') },
      ...rungs.map((r) => ({ value: r.key, label: t(r.label), disabled: notHeld(r.key) })),
    ];
    const allRungsNotHeld = rungs.length > 0 && rungs.every((r) => notHeld(r.key));
    const included = currentKey !== null ? [...expandIncludes([currentKey])].filter((k) => k !== currentKey) : [];
    const includedLabels = rungs.filter((r) => included.includes(r.key)).map((r) => t(r.label));
    const adjuncts = (th.adjuncts ?? []).filter((a) => !entOff(a.key));
    const inertHere = currentKey ? inert.find((x) => x.key === currentKey) : undefined;
    const scaleLabel = t(th.label);
    // The PRIMARY scale (name == the family) drops its label: the family header
    // already names it, so the control sits flush-left, aligned with the adjuncts
    // below (no floating, fixed-width-offset control). A SECONDARY scale (e.g. the
    // Assets family's "Financials") keeps its label to disambiguate.
    const showLabel = scaleLabel !== famLabel;

    const labelRow = (showLabel || th.help) && (
      <span className="inline-flex items-center gap-1 text-sm">
        {showLabel && scaleLabel}
        {th.help && <HelpHint text={t(th.help)} label={scaleLabel} />}
      </span>
    );

    return (
      <div key={th.id}>
        {/* The label row stays even when no rung is offered (a surface offering
            only a scale's adjuncts) — the adjuncts below still get their name. */}
        {rungs.length === 0 && labelRow && <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5">{labelRow}</div>}
        {rungs.length > 0 && (
          <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5">
            {labelRow}
            <SegmentedControl
              ariaLabel={allRungsNotHeld ? `${scaleLabel} — ${t('editor.notHeld')}` : scaleLabel}
              options={options}
              value={currentKey ?? NONE}
              disabled={readOnly}
              onChange={(v) => setScale(rungs.map((r) => r.key), v === NONE ? null : v)}
            />
            {allRungsNotHeld && (
              <Badge variant="neutral" appearance="subtle">
                {t('editor.notHeld')}
              </Badge>
            )}
            {includedLabels.length > 0 && (
              <span className="inline-flex items-center gap-1 text-xs text-[var(--color-text-secondary)]">
                <icons.check size={12} aria-hidden /> {t('editor.includes', { labels: includedLabels.join(' · ') })}
              </span>
            )}
            {inertHere && (
              <span className="inline-flex items-center gap-1.5 text-xs text-[var(--color-text-warning)]">
                <icons.statusWarning size={12} aria-hidden /> {t('editor.needs', { labels: joinLabels(inertHere.missing) })}
                {fixLink(inertHere.missing, 'text-[var(--color-primary-display)] underline', t('editor.fix'))}
              </span>
            )}
          </div>
        )}
        {adjuncts.length > 0 && (
          <div className={rungs.length > 0 ? 'mt-2.5 flex flex-col gap-2' : 'flex flex-col gap-2'}>
            {adjuncts.map((a) => renderAdjunct(a.key, a.label, a.help))}
          </div>
        )}
      </div>
    );
  };

  const renderAdjunct = (key: PermissionKey, labelKey: string, helpKey?: string): ReactNode => {
    const on = grantSet.has(key);
    const isInert = inertSet.has(key);
    const missing = inert.find((x) => x.key === key)?.missing ?? [];
    const reqs = getCapability(key)?.requires ?? [];
    const held = !notHeld(key);
    return (
      <div key={key}>
        <div className="flex items-center gap-2">
          <Checkbox checked={on} disabled={readOnly || !held} onChange={(v) => toggleKey(key, v)} label={withNotHeld(t(labelKey), held)} />
          {helpKey && <HelpHint text={t(helpKey)} label={t(labelKey)} />}
          {held && !on && reqs.length > 0 && !reqMet(key) && (
            <span className="ms-auto text-xs text-[var(--color-text-secondary)]">{t('editor.needs', { labels: joinLabels(reqs) })}</span>
          )}
          {on && isInert && (
            <span className="ms-auto">
              <Badge variant="warning" appearance="subtle">
                {t('editor.inert')}
              </Badge>
            </span>
          )}
        </div>
        {on && isInert && fixLink(missing, 'ms-6 text-xs text-[var(--color-primary-display)] underline', t('editor.set', { labels: joinLabels(missing) }))}
        {renderNarrowing?.(key, on)}
      </div>
    );
  };

  const renderToggle = (th: ToggleThing): ReactNode => {
    const on = grantSet.has(th.key);
    const isInert = inertSet.has(th.key);
    const missing = inert.find((x) => x.key === th.key)?.missing ?? [];
    const held = !notHeld(th.key);
    return (
      <div key={th.id}>
        <div className="flex items-center gap-2">
          <Toggle checked={on} disabled={readOnly || !held} onChange={(v) => toggleKey(th.key, v)} label={withNotHeld(t(th.label), held)} />
          {th.help && <HelpHint text={t(th.help)} label={t(th.label)} />}
          {th.highStakes && (
            <Badge variant="warning" appearance="subtle">
              {t('editor.highImpact')}
            </Badge>
          )}
          {on && isInert && (
            <span className="ms-auto">
              <Badge variant="warning" appearance="subtle">
                {t('editor.inert')}
              </Badge>
            </span>
          )}
        </div>
        {on && isInert && fixLink(missing, 'ms-12 mt-1 text-xs text-[var(--color-primary-display)] underline', t('editor.set', { labels: joinLabels(missing) }))}
        {renderNarrowing?.(th.key, on)}
      </div>
    );
  };

  const renderThing = (th: Thing, famLabel: string): ReactNode =>
    th.kind === 'scale' ? renderScale(th, famLabel) : renderToggle(th);

  const renderFamily = (fam: Family): ReactNode => {
    const Glyph = icons[fam.icon];
    const famLabel = t(fam.label);
    const moduleOff = fam.entitlement !== undefined && !entitlements.has(fam.entitlement);
    const head = (
      <>
        <Glyph size={16} aria-hidden className="shrink-0 text-[var(--color-text-secondary)]" />
        <span className="flex-1 truncate text-sm font-medium">{famLabel}</span>
      </>
    );

    // FULLY UNAVAILABLE — module off, or a draft module not yet built. A muted,
    // non-interactive row (caret matches a collapsed Disclosure for alignment) naming
    // the module / "coming soon" — one chip vocabulary, never a ghost control.
    if (moduleOff || fam.comingSoon) {
      const chip =
        moduleOff && fam.entitlement ? t('editor.moduleOff', { module: moduleLabel(fam.entitlement) }) : t('editor.comingSoon');
      return (
        <div key={fam.id} className="flex min-h-[2.5rem] items-center gap-2.5 px-4 py-3 opacity-60">
          <icons.caretDown size={14} aria-hidden className="shrink-0 -rotate-90 text-[var(--color-text-tertiary)] rtl:rotate-90" />
          {head}
          <Badge variant="neutral" appearance="subtle">
            {chip}
          </Badge>
        </div>
      );
    }

    const things = fam.things ?? [];
    const allKeys = things.flatMap(keysOfThing);
    const hiddenKeys = allKeys.filter((k) => entOff(k));
    // The count names what the panel actually shows — a granted key hidden behind
    // an off module isn't a tick the admin can see.
    const granted = allKeys.filter((k) => grantSet.has(k) && !entOff(k)).length;
    // A narrowing that lives on while its key is OFF (a credential's stored
    // device-target kinds — the dormant state) must not hide behind a collapsed,
    // unbadged row: such a family opens by rule, so the dormant list is in view.
    const dormantNarrowing = allKeys.some((k) => !grantSet.has(k) && renderNarrowing?.(k, false) != null);
    const hiddenMods = [...new Set(hiddenKeys.map((k) => getCapability(k)?.entitlement).filter(Boolean))] as EntitlementFlag[];

    return (
      <Disclosure
        key={fam.id}
        flush
        // Open all families COLLAPSED — the editor is a scan-then-expand surface;
        // auto-expanding a couple of families bloated the initial view + implied
        // they matter more. A granted-count Badge already flags which hold grants.
        // The one exception is a dormant narrowing (above): nothing else signals it.
        defaultExpanded={dormantNarrowing}
        level={disclosureLevel}
        leading={<Glyph size={16} aria-hidden className="shrink-0 text-[var(--color-text-secondary)]" />}
        title={famLabel}
        trailing={
          granted > 0 ? (
            <Badge variant="brand" appearance="subtle">
              {formatNumber(granted)}
            </Badge>
          ) : undefined
        }
      >
        {/* Tier-1 help: a one-line family intro shown on expand (in the content, not
            the trigger — so it never inflates the collapsed header). */}
        {fam.help && <p className="mb-4 text-xs text-[var(--color-text-secondary)]">{t(fam.help)}</p>}
        <div className="flex flex-col gap-5">
          {things.map((th) => renderThing(th, famLabel))}
          {hiddenMods.length > 0 && (
            <p className="flex items-center gap-1.5 text-xs text-[var(--color-text-secondary)]">
              <icons.hide size={13} aria-hidden />
              {t('editor.hidden', { modules: hiddenMods.map(moduleLabel).join(', '), count: hiddenKeys.length })}
            </p>
          )}
        </div>
      </Disclosure>
    );
  };

  return (
    <div className="flex flex-col gap-6">
      {sections.map((sec) => (
        <section key={sec.id}>
          <SectionHeading className="mb-2 px-1 text-xs font-medium uppercase tracking-wide text-[var(--color-text-secondary)]">
            {t(sec.label)}
          </SectionHeading>
          <div className="divide-y divide-[var(--color-border)] overflow-hidden rounded-lg border border-[var(--color-border)] bg-[var(--color-bg-surface)]">
            {sec.families.map((fam) => renderFamily(fam))}
          </div>
        </section>
      ))}
    </div>
  );
}

// Flat key → contextual i18n-key parts for the "needs X" / Fix copy. A scale rung
// carries its scale label key + rung key, so a cross-scale prerequisite renders as
// "Assets: View" once translated; adjuncts / toggles carry a single label key.
type KeyMeta = { scaleKey: string; rungKey: string } | { labelKey: string };
const keyMeta = new Map<string, KeyMeta>();
for (const sec of SECTIONS) {
  for (const fam of sec.families) {
    for (const th of fam.things ?? []) {
      if (th.kind === 'scale') {
        th.rungs.forEach((r) => keyMeta.set(r.key, { scaleKey: th.label, rungKey: r.label }));
        (th.adjuncts ?? []).forEach((a) => keyMeta.set(a.key, { labelKey: a.label }));
      } else {
        keyMeta.set(th.key, { labelKey: th.label });
      }
    }
  }
}
